import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormikPasswordInput, ButtonGroup, passwordValidation, UserInfo } from '@td-client-frontend/common';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../components/Button';
import { useAxios } from '../context/AxiosContext';
import { usePasswordTokenValidation } from '../hooks/usePassword';
import { WithError } from '../context/AuthContext';
import { Nullable, PiwikAction, PiwikCategory, PiwikName, Errors } from '../@types/common';
import { Tooltip } from '../components/Tooltip';
import { AxiosError } from 'axios';
import { trackEvent } from '../api/piwik/piwikService';

export function ResetPassword() {
  const history = useHistory();
  const { token } = useParams<{ token: string }>();
  const axiosContext = useAxios();
  const { validateToken } = usePasswordTokenValidation();

  const navigateToRoot = useCallback(() => history.push('/'), [history]);

  const [tasErrors, setTasErrors] = useState<Nullable<string[]>>(null);

  const handleInvalidToken = useCallback(
    (message) => {
      toast.error(message, {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true,
        autoClose: 10000
      });
      history.push('/forgot-password');
    },
    [history]
  );

  useEffect(() => {
    let isCurrent = true;
    if (token) {
      validateToken(token)
        .then((res) => {
          if (isCurrent) {
            if (res.reset_password_flg !== 'Y') {
              toast.info('The link is expired. Please submit a new reset password request.', {
                position: 'top-center',
                hideProgressBar: true,
                autoClose: false
              });
              history.push('/forgot-password');
            }
          }
        })
        .catch((e) => {
          if (isCurrent) {
            handleInvalidToken(e.message);
          }
        });
    }

    return () => {
      isCurrent = false;
    };
  }, [handleInvalidToken, history, token, validateToken]);

  return (
    <div
      className="flex flex-col justify-start w-full h-full max-w-2xl mx-auto font-effra"
      data-testid="registerContainer"
    >
      <div className="flex items-center">
        <h2 className="my-6 mr-2 ml-6 text-4xl font-medium leading-tight font-effra">Set password</h2>
        <Tooltip
          type="restricted-words-in-password"
          text="Password cannot contain the words “Teladoc”, “Password”, or previously used password phrases."
        />
      </div>

      <Formik
        initialValues={{
          newPassword: '',
          verifyNewPassword: ''
        }}
        enableReinitialize
        validationSchema={Yup.object({
          // @ts-ignore
          newPassword: passwordValidation,
          // @ts-ignore
          verifyNewPassword: passwordValidation.oneOf([Yup.ref('newPassword'), ''], 'Passwords must match')
        })}
        onSubmit={async (values, { setSubmitting }) => {
          trackEvent({
            category: PiwikCategory.BUTTONS,
            action: PiwikAction.CLICKED,
            name: PiwikName.RESET_PASSWORD
          });
          setSubmitting(true);
          setTasErrors(null);

          try {
            const response = await axiosContext.authAxios.post<WithError<UserInfo>>('/change-password-by-token', {
              password: values.newPassword,
              confirmPassword: values.verifyNewPassword,
              token
            });

            if (response?.data?.uid) {
              toast.success('Your password has been set successfully!', {
                position: 'top-center',
                hideProgressBar: true,
                pauseOnHover: true,
                draggable: true,
                autoClose: 10000
              });
              navigateToRoot();
            }
          } catch (e) {
            setTasErrors((e as AxiosError<WithError<UserInfo>>).response?.data?.errors || null);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, isValid, errors }) => (
          <Form data-testid="reset-pass-form" className="p-8 m-6 bg-white shadow-lg round-lg mb-l">
            {tasErrors && (
              <div className="mb-8 text-center text-red-500">
                {tasErrors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            )}
            {(errors.newPassword || errors.verifyNewPassword) === Errors.InvalidPasswordEntered && (
              <div className="flex mb-4">
                <p>&#128683;</p>
                <p className="w-2/3 ml-2" data-testid="restricted-word-notifier">
                  Password cannot contain the words “Teladoc”, “Password”, or previously used password phrases. Please
                  choose a new password.
                </p>
              </div>
            )}

            <FormikPasswordInput
              label="New Password"
              isRequired
              placeholder="Please enter password"
              data-testid="new-password"
              name="newPassword"
            />
            <FormikPasswordInput
              label="Verify New Password"
              isRequired
              placeholder="Please enter password again"
              data-testid="verify-new-password"
              name="verifyNewPassword"
            />
            <ButtonGroup>
              <Button data-testid="submitButton" disabled={isSubmitting || !isValid} kind="primary" type="submit">
                Confirm
              </Button>
              <Button
                data-testid="cancelButton"
                disabled={isSubmitting}
                kind="secondary"
                onClick={navigateToRoot}
                name="cancel"
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ResetPassword;
